import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { Code2, KeyRound, Zap, ArrowRight, Terminal } from "lucide-react";

const BASE = "https://afrimarket.org/api/v1";

const CodeBlock = ({ children }) => (
  <pre className="bg-[#1A2024] text-[#E8E2D6] rounded-lg p-4 text-xs sm:text-sm overflow-x-auto my-3 leading-relaxed">
    <code>{children}</code>
  </pre>
);

const Endpoint = ({ method, path, desc, scope, example, testid }) => {
  const color = method === "GET" ? "bg-[#2A3D36]" : method === "POST" ? "bg-[#C05A3C]" : "bg-[#D9923B]";
  return (
    <div className="border border-black/10 rounded-lg bg-white p-5 mb-4" data-testid={testid}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`${color} text-white text-xs font-bold px-2.5 py-1 rounded`}>{method}</span>
        <code className="text-sm font-semibold break-all">{path}</code>
        <span className="text-xs text-muted-foreground ml-auto">scope: <b>{scope}</b></span>
      </div>
      <p className="text-sm text-muted-foreground mt-2">{desc}</p>
      {example && <CodeBlock>{example}</CodeBlock>}
    </div>
  );
};

const DeveloperDocs = () => {
  return (
    <div className="heritage-canvas min-h-screen">
      <SEO title="Developer API Docs — AfriMarket" description="Integrate with AfriMarket: browse the catalogue, manage listings and orders via our REST API." path="/developers" />
      <div className="container-narrow py-12">
        {/* Hero */}
        <div className="mb-10">
          <div className="label-overline text-[#C05A3C] mb-3 flex items-center gap-2"><Code2 className="w-4 h-4" /> Developer Platform</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight">AfriMarket API</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl text-base">
            A simple REST API for partners, affiliates, suppliers and apps. Read the live catalogue of African products and services, and manage your own listings and orders programmatically.
          </p>
          <div className="flex gap-3 mt-6">
            <Link to="/dashboard/developer" data-testid="docs-get-key-btn">
              <Button className="bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md"><KeyRound className="w-4 h-4 mr-2" /> Get your API key <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </Link>
          </div>
        </div>

        {/* Quick facts */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="bg-white border border-black/10 rounded-lg p-5">
            <Terminal className="w-5 h-5 text-[#C05A3C] mb-2" />
            <div className="font-semibold text-sm">Base URL</div>
            <code className="text-xs text-muted-foreground break-all">{BASE}</code>
          </div>
          <div className="bg-white border border-black/10 rounded-lg p-5">
            <KeyRound className="w-5 h-5 text-[#C05A3C] mb-2" />
            <div className="font-semibold text-sm">Auth header</div>
            <code className="text-xs text-muted-foreground break-all">X-API-Key: afm_live_…</code>
          </div>
          <div className="bg-white border border-black/10 rounded-lg p-5">
            <Zap className="w-5 h-5 text-[#C05A3C] mb-2" />
            <div className="font-semibold text-sm">Rate limit</div>
            <span className="text-xs text-muted-foreground">120 requests / minute per key</span>
          </div>
        </div>

        {/* Auth */}
        <h2 className="font-display text-2xl mt-10 mb-2">Authentication</h2>
        <p className="text-sm text-muted-foreground mb-2">
          Create a key in your <Link to="/dashboard/developer" className="text-[#C05A3C] underline">Developer dashboard</Link>. Send it on every request using the <code>X-API-Key</code> header (or <code>Authorization: Bearer</code>). Read-only keys can browse the catalogue; write keys can also manage your own listings and orders.
        </p>
        <CodeBlock>{`curl ${BASE}/listings \\
  -H "X-API-Key: afm_live_your_key_here"`}</CodeBlock>

        {/* Read endpoints */}
        <h2 className="font-display text-2xl mt-10 mb-4">Read endpoints</h2>
        <Endpoint
          method="GET" scope="read" testid="ep-list-listings"
          path="/v1/listings"
          desc="Search & list active listings. Query params: q, type (product|service), category, country, limit (1–100), offset."
          example={`curl "${BASE}/listings?type=product&country=United%20Kingdom&limit=10" \\
  -H "X-API-Key: afm_live_..."`}
        />
        <Endpoint
          method="GET" scope="read" testid="ep-get-listing"
          path="/v1/listings/{listing_id}"
          desc="Fetch a single listing with full details, images and seller info."
          example={`curl "${BASE}/listings/lst_abc123" -H "X-API-Key: afm_live_..."`}
        />
        <Endpoint
          method="GET" scope="read" testid="ep-categories"
          path="/v1/categories"
          desc="All product and service categories used across the marketplace."
        />
        <Endpoint
          method="GET" scope="read" testid="ep-shops"
          path="/v1/shops"
          desc="List seller storefronts (shops)."
        />
        <Endpoint
          method="GET" scope="read" testid="ep-me"
          path="/v1/me"
          desc="Details about the account the key belongs to, plus scopes and rate limit."
        />

        {/* Write endpoints */}
        <h2 className="font-display text-2xl mt-10 mb-4">Write endpoints <span className="text-sm font-sans-ui text-muted-foreground">(require a write key + verified seller)</span></h2>
        <Endpoint
          method="POST" scope="write" testid="ep-create-listing"
          path="/v1/listings"
          desc="Publish a new listing (goes to admin approval, like the website). Body: type, title, description, category, price, currency, country, city, images[]."
          example={`curl -X POST "${BASE}/listings" \\
  -H "X-API-Key: afm_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "type": "product",
    "title": "Handwoven Ankara Tote",
    "description": "Bright African-print tote bag.",
    "category": "Bags & Accessories",
    "price": 34.99,
    "currency": "GBP",
    "country": "United Kingdom",
    "city": "London",
    "images": ["https://.../tote.jpg"]
  }'`}
        />
        <Endpoint
          method="PATCH" scope="write" testid="ep-update-listing"
          path="/v1/listings/{listing_id}"
          desc="Update one of your own listings. Send only the fields you want to change. Set status to 'active' or 'inactive'."
          example={`curl -X PATCH "${BASE}/listings/lst_abc123" \\
  -H "X-API-Key: afm_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"price": 29.99, "status": "active"}'`}
        />
        <Endpoint
          method="GET" scope="write" testid="ep-orders"
          path="/v1/orders"
          desc="List your paid orders (sales), with shipping address, amounts and fulfilment status."
          example={`curl "${BASE}/orders?limit=20" -H "X-API-Key: afm_live_..."`}
        />
        <Endpoint
          method="PATCH" scope="write" testid="ep-fulfilment"
          path="/v1/orders/{order_id}/fulfilment"
          desc="Update fulfilment: 'packed', 'shipped' (tracking_number required for physical goods) or 'delivered'."
          example={`curl -X PATCH "${BASE}/orders/txn_123/fulfilment" \\
  -H "X-API-Key: afm_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"fulfilment_status": "shipped", "tracking_number": "RM123456789GB"}'`}
        />

        {/* Errors */}
        <h2 className="font-display text-2xl mt-10 mb-2">Errors & limits</h2>
        <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
          <li><b>401</b> — missing/invalid/revoked key.</li>
          <li><b>403</b> — key lacks the required scope (e.g. write) or account isn’t a verified seller.</li>
          <li><b>404</b> — resource not found or not owned by you.</li>
          <li><b>429</b> — rate limit exceeded (120 req/min). Retry after a short pause.</li>
        </ul>

        <div className="mt-12 bg-[#1A2024] text-white rounded-xl p-8 text-center">
          <h3 className="font-display text-2xl mb-2">Ready to build?</h3>
          <p className="text-white/70 text-sm mb-5">Generate a key and make your first request in under a minute.</p>
          <Link to="/dashboard/developer">
            <Button className="bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md"><KeyRound className="w-4 h-4 mr-2" /> Get your API key</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DeveloperDocs;
